/**
 * The invoices a parent can pick on the "Pay fees" sheet.
 *
 * "Pay now" under Total outstanding (KYD 6,487.50) used to open ONE instalment - in the
 * app, the first with a balance (KYD 280, unnamed); on the web, only what was already
 * due - so the amount never matched the card and the parent could not choose (QA). Now
 * the sheet lists every pending invoice, all ticked from "Pay now" (total = outstanding),
 * or just the one from a row's own button, and the parent can change the selection.
 */
export type PayTag = 'Overdue' | 'Due soon' | 'Upcoming';

export interface PayItem {
  id: string;
  label: string;
  dueText: string;
  amount: number;
  tag: PayTag;
}

interface PayableLike {
  id: string;
  status: string;
  balanceAmount: number;
  dueDate: string;
}

const DUE_SOON_DAYS = 30;

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

/** Overdue (status, or due before today), Due soon (within 30 days), otherwise Upcoming. */
export function payTag(status: string, dueDate: string, today: Date = new Date()): PayTag {
  if (status === 'overdue') return 'Overdue';
  const due = new Date(dueDate);
  if (isNaN(due.getTime())) return 'Due soon';
  const days = Math.round((startOfDay(due).getTime() - startOfDay(today).getTime()) / 86_400_000);
  if (days < 0) return 'Overdue';
  return days <= DUE_SOON_DAYS ? 'Due soon' : 'Upcoming';
}

/** Every invoice that still has a balance, soonest due first. */
export function payItemsFrom<T extends PayableLike>(
  installments: T[],
  labelOf: (inst: T) => string,
  dueTextOf: (inst: T) => string,
  today: Date = new Date(),
): PayItem[] {
  return installments
    .filter(i => i.status !== 'paid' && i.balanceAmount > 0)
    .sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime())
    .map(i => ({
      id: i.id,
      label: labelOf(i),
      dueText: dueTextOf(i),
      amount: i.balanceAmount,
      tag: payTag(i.status, i.dueDate, today),
    }));
}

/** The total of the ticked invoices, to the cent. */
export function selectedTotal(items: PayItem[], selected: ReadonlySet<string>): number {
  const cents = items.filter(i => selected.has(i.id)).reduce((sum, i) => sum + Math.round(i.amount * 100), 0);
  return cents / 100;
}

/** "CXC/CSEC Registration - May/June 2027" for one, "3 invoices selected" for several. */
export function selectionSummary(items: PayItem[], selected: ReadonlySet<string>): string {
  const picked = items.filter(i => selected.has(i.id));
  if (picked.length === 0) return 'No invoice selected';
  if (picked.length === 1) return picked[0].label;
  return `${picked.length} invoices selected`;
}
